import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

// Legal transitions from each status. Buttons for illegal ones are simply
// not rendered (per user requirement — hide, don't disable).
const LEGAL_ACTIONS = {
  draft: ['publish', 'cancel'],
  published: ['cancel', 'complete'],
  cancelled: ['archive'],
  completed: ['archive'],
  archived: [],
};

const ACTION_META = {
  publish: { label: 'Publish', variant: 'default', tone: 'bg-emerald-600 hover:bg-emerald-700 text-white', prompt: null, testid: 'lifecycle-publish' },
  cancel: { label: 'Cancel Event', variant: 'outline', tone: '', prompt: { title: 'Cancel event', desc: 'Provide a reason. The event will move to Cancelled and remain visible for the audience.', required: true }, testid: 'lifecycle-cancel' },
  complete: { label: 'Mark Completed', variant: 'outline', tone: '', prompt: null, testid: 'lifecycle-complete' },
  archive: { label: 'Archive', variant: 'outline', tone: '', prompt: { title: 'Archive event', desc: 'Optional reason. Archived events are hidden from standard feeds.', required: false }, testid: 'lifecycle-archive' },
};

export default function LifecycleActions({ status, onAction, publicationReady, disabled = false }) {
  const [reasonDialog, setReasonDialog] = useState(null); // { action, required, title, desc }
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const legal = LEGAL_ACTIONS[status] || [];
  if (legal.length === 0) return null;

  const invoke = async (action, providedReason) => {
    setBusy(true);
    try {
      await onAction(action, providedReason);
    } finally {
      setBusy(false);
      setReasonDialog(null);
      setReason('');
    }
  };

  const handleClick = (action) => {
    const meta = ACTION_META[action];
    if (meta.prompt) {
      setReason('');
      setReasonDialog({ action, ...meta.prompt });
    } else {
      invoke(action);
    }
  };

  return (
    <div className="flex flex-wrap gap-2" data-testid="lifecycle-actions">
      {legal.map((action) => {
        const meta = ACTION_META[action];
        const isPublishBlocked = action === 'publish' && publicationReady === false;
        return (
          <Button
            key={action}
            size="sm"
            variant={meta.variant}
            className={meta.tone}
            onClick={() => handleClick(action)}
            disabled={disabled || busy || isPublishBlocked}
            data-testid={meta.testid}
            title={isPublishBlocked ? 'Resolve blocking errors first' : undefined}
          >
            {meta.label}
          </Button>
        );
      })}

      <AlertDialog open={!!reasonDialog} onOpenChange={(o) => !o && setReasonDialog(null)}>
        <AlertDialogContent data-testid="lifecycle-reason-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{reasonDialog?.title}</AlertDialogTitle>
            <AlertDialogDescription>{reasonDialog?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="lifecycle-reason">Reason {reasonDialog?.required && <span className="text-rose-600">*</span>}</Label>
            <Textarea
              id="lifecycle-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonDialog?.required ? 'Required' : 'Optional'}
              data-testid="lifecycle-reason-input"
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lifecycle-reason-cancel">Back</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (reasonDialog?.required && !reason.trim()) return;
                invoke(reasonDialog.action, reason.trim() || undefined);
              }}
              disabled={busy || (reasonDialog?.required && !reason.trim())}
              data-testid="lifecycle-reason-confirm"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
